import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { IconProvider } from '@/components/ui';
import { DialogField, getCachedDialogs } from '@/lib/stores/dialogStore';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface ExternalDialogFieldEditorProps {
  field: DialogField;
  fieldIndex: number;
  sectionIndex: number;
  currentDialogId?: string;
  dialogTranslatable: boolean;
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  onRemoveField: (sectionIndex: number, fieldId: string) => void;
  gameId?: string;
  t: any;
}

/**
 * ExternalDialogFieldEditor handles 'external-dialog' field type
 * Allows jumping to another dialog (and optionally a specific section/node within it)
 */
export const ExternalDialogFieldEditor = React.memo(function ExternalDialogFieldEditor({
  field,
  fieldIndex,
  sectionIndex,
  currentDialogId,
  dialogTranslatable,
  onUpdateField,
  onRemoveField,
  gameId,
  t,
}: ExternalDialogFieldEditorProps) {
  const { selectedGame } = useSimplifiedUserData();
  const effectiveGameId = gameId || selectedGame?.id;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  // Get all available dialogs (excluding current dialog)
  const availableDialogs = useMemo(() => {
    if (!effectiveGameId) return [];
    const allDialogs = getCachedDialogs(effectiveGameId);
    return allDialogs.filter(d => d.id !== currentDialogId);
  }, [effectiveGameId, currentDialogId]);

  // Get target dialog details
  const targetDialog = useMemo(() => {
    if (!field.next_dialog || !effectiveGameId) return null;
    return getCachedDialogs(effectiveGameId).find(d => d.id === field.next_dialog);
  }, [field.next_dialog, effectiveGameId]);

  // Get available sections/nodes in the target dialog
  const availableSections = useMemo(() => {
    if (!targetDialog) return [];
    return targetDialog.data?.sections || [];
  }, [targetDialog]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 p-2 border border-gray-200 rounded-sm bg-white"
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 pt-2"
        type="button"
      >
        <IconProvider iconstring="fa-grip-vertical" size={14} />
      </button>

      {/* Field Content */}
      <div className="flex-1 items-center justify-center space-y-2">
        {/* External Dialog Configuration */}
        <div className="space-y-3 bg-blue-50 p-3 rounded border border-blue-200">
          <Label className="text-sm font-semibold flex items-center gap-2">
            <IconProvider iconstring="fa-arrow-right-to-bracket" size={14} />
            Jump to External Dialog
          </Label>

          {/* Dialog Selection */}
          <div className="space-y-2 bg-white p-2 rounded border border-blue-300">
            <Label className="text-xs text-gray-600">Target dialog:</Label>
            {availableDialogs.length > 0 ? (
              <Select
                value={field.next_dialog || ''}
                onValueChange={(value) => {
                  onUpdateField(sectionIndex, field.id, {
                    next_dialog: value,
                    next_dialog_section: null, // Reset section when dialog changes
                  });
                }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select dialog..." />
                </SelectTrigger>
                <SelectContent>
                  {availableDialogs.map((dialog) => (
                    <SelectItem key={dialog.id} value={dialog.id}>
                      {dialog.name} ({dialog.dialog_key})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-xs text-gray-500 italic p-2">
                No other dialogs available in this game
              </div>
            )}
          </div>

          {/* Section/Node Selection (optional) */}
          {field.next_dialog && availableSections.length > 0 && (
            <div className="space-y-2 bg-white p-2 rounded border border-blue-300">
              <Label className="text-xs text-gray-600">Target section (optional):</Label>
              <Select
                value={field.next_dialog_section || '__default__'}
                onValueChange={(value) => {
                  onUpdateField(sectionIndex, field.id, {
                    next_dialog_section: value === '__default__' ? null : value,
                  });
                }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Start of dialog" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">Start of dialog</SelectItem>
                  {availableSections.map((section) => (
                    <SelectItem key={section.section_identifier} value={section.section_identifier}>
                      {section.name || 'Unnamed Section'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Selected Dialog Info */}
          {targetDialog && (
            <div className="text-xs text-blue-700 bg-blue-100 p-2 rounded border border-blue-300">
              <div className="flex items-start gap-2">
                <IconProvider iconstring="fa-info-circle" size={12} className="mt-0.5" />
                <div>
                  <div className="font-semibold">Will jump to: {targetDialog.name}</div>
                  {field.next_dialog_section && (
                    <div className="text-blue-600">
                      Section: {availableSections.find(s => s.section_identifier === field.next_dialog_section)?.name || 'Unknown'}
                    </div>
                  )}
                  <div className="text-blue-600 font-mono text-xs mt-1">
                    dialog_key: {targetDialog.dialog_key}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Field Type & Buttons */}
      <div className="flex items-center gap-1">
        {/* External dialog fields don't need ConnectionBadge - they are endpoints */}
        {/* Remove Field Button */}
        <Button
          variant="halfghost"
          size="sm"
          onClick={() => onRemoveField(sectionIndex, field.id)}
          className="px-2 text-xs w-8 h-8"
          type="button"
        >
          <IconProvider
            iconstring="fa-xmark"
            size={14}
            color="currentColor"
            className="hover:opacity-80 transition-colors"
          />
        </Button>
      </div>
    </div>
  );
});
